/**
 * Professional Logging Utility
 *
 * Features:
 * - Structured logging with levels (TRACE, DEBUG, INFO, WARN, ERROR)
 * - Sentry integration for error tracking
 * - Correlation IDs for request tracing
 * - Context metadata support
 * - Performance metrics
 * - Production-safe (configurable verbosity)
 *
 * Usage:
 *   logger.info('User logged in', { userId: 123 })
 *   logger.error('Payment failed', error, { amount: 100 })
 *   logger.metric('api_call', { endpoint: '/rica', duration: 234 })
 */

import * as Sentry from '@sentry/react';

// Log levels
export const LOG_LEVELS = {
  TRACE: 0,
  DEBUG: 1,
  INFO: 2,
  WARN: 3,
  ERROR: 4,
  SILENT: 5,
};

// Configuration
const CONFIG = {
  level: import.meta.env.PROD ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG,
  enableConsole: !import.meta.env.PROD,
  enableSentry: import.meta.env.PROD,
  enableMetrics: true,
};

// Global context (can be set by correlation ID provider)
let globalContext = {};

class Logger {
  constructor(name = 'app') {
    this.name = name;
    this.startTime = Date.now();
  }

  /**
   * Set global context for all log messages
   * @param {Object} context - Context object (e.g., { correlationId, userId })
   */
  setContext(context) {
    globalContext = { ...globalContext, ...context };
  }

  /**
   * Clear global context
   */
  clearContext() {
    globalContext = {};
  }

  /**
   * Get current context
   */
  getContext() {
    return { ...globalContext };
  }

  /**
   * Internal log method
   * @private
   */
  _log(level, levelName, message, meta = {}) {
    if (level < CONFIG.level) return;

    const safeMeta = meta && typeof meta === 'object' ? meta : {};

    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level: levelName,
      logger: this.name,
      message,
      ...globalContext,
      ...safeMeta,
    };

    // Console output (development)
    if (CONFIG.enableConsole) {
      const consoleMethod = this._getConsoleMethod(level);
      const prefix = `[${timestamp}] [${levelName}] [${this.name}]`;

      if (Object.keys(safeMeta).length > 0) {
        consoleMethod(prefix, message, safeMeta);
      } else {
        consoleMethod(prefix, message);
      }
    }

    // Sentry integration (production errors/warnings)
    if (CONFIG.enableSentry && level >= LOG_LEVELS.WARN) {
      this._sendToSentry(level, message, safeMeta);
    }

    // Metrics collection (guard against null/undefined safeMeta from edge cases)
    if (CONFIG.enableMetrics && safeMeta && safeMeta.metric) {
      this._recordMetric(message, safeMeta);
    }

    return logEntry;
  }

  /**
   * Get appropriate console method for log level
   * @private
   */
  _getConsoleMethod(level) {
    switch (level) {
      case LOG_LEVELS.TRACE:
      case LOG_LEVELS.DEBUG:
        return console.debug;
      case LOG_LEVELS.INFO:
        return console.info;
      case LOG_LEVELS.WARN:
        return console.warn;
      case LOG_LEVELS.ERROR:
        return console.error;
      default:
        return console.log;
    }
  }

  /**
   * Send to Sentry
   * @private
   */
  _sendToSentry(level, message, meta) {
    const actualError = meta?.error instanceof Error ? meta.error :
                        meta instanceof Error ? meta : null;
    if (level === LOG_LEVELS.ERROR && actualError) {
      Sentry.captureException(actualError, {
        contexts: {
          logger: {
            name: this.name,
            message,
            ...globalContext,
            ...meta,
          },
        },
        tags: {
          logger: this.name,
        },
      });
    } else {
      Sentry.captureMessage(message, {
        level: level === LOG_LEVELS.ERROR ? 'error' : 'warning',
        contexts: {
          logger: {
            name: this.name,
            ...globalContext,
            ...meta,
          },
        },
      });
    }
  }

  /**
   * Record metric to local storage for analytics
   * @private
   */
  _recordMetric(metricName, data) {
    try {
      const metrics = JSON.parse(localStorage.getItem('app_metrics') || '[]');
      metrics.push({
        name: metricName,
        timestamp: Date.now(),
        ...data,
      });

      // Keep only last 1000 metrics
      if (metrics.length > 1000) {
        metrics.splice(0, metrics.length - 1000);
      }

      localStorage.setItem('app_metrics', JSON.stringify(metrics));
    } catch (e) {
      // Fail silently
    }
  }

  // Public logging methods
  trace(message, meta) {
    return this._log(LOG_LEVELS.TRACE, 'TRACE', message, meta);
  }

  debug(message, meta) {
    return this._log(LOG_LEVELS.DEBUG, 'DEBUG', message, meta);
  }

  info(message, meta) {
    return this._log(LOG_LEVELS.INFO, 'INFO', message, meta);
  }

  warn(message, meta) {
    return this._log(LOG_LEVELS.WARN, 'WARN', message, meta);
  }

  error(message, errorOrMeta, additionalMeta = {}) {
    // Support both error(msg, error, meta) and error(msg, meta)
    const isError = errorOrMeta instanceof Error;
    const meta = isError ? { error: errorOrMeta, ...additionalMeta } : errorOrMeta;

    return this._log(LOG_LEVELS.ERROR, 'ERROR', message, meta);
  }

  /**
   * Log a business metric
   * @param {string} metricName - Name of the metric
   * @param {Object} data - Metric data
   */
  metric(metricName, data = {}) {
    return this._log(LOG_LEVELS.INFO, 'METRIC', metricName, {
      ...data,
      metric: true,
    });
  }

  /**
   * Measure execution time of a function
   * @param {string} label - Label for the measurement
   * @param {Function} fn - Function to measure
   * @returns {*} - Result of the function
   */
  async measure(label, fn) {
    const start = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - start;
      this.metric('performance', {
        operation: label,
        duration: Math.round(duration),
        success: true,
      });
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      this.metric('performance', {
        operation: label,
        duration: Math.round(duration),
        success: false,
      });
      this.error(`${label} failed`, error);
      throw error;
    }
  }

  /**
   * Create a child logger with a specific name
   * @param {string} name - Logger name
   * @returns {Logger}
   */
  child(name) {
    return new Logger(`${this.name}.${name}`);
  }
}

// Default export - singleton instance
const logger = new Logger('megsapp-portal');

export default logger;

// Named exports for convenience
export const trace = logger.trace.bind(logger);
export const debug = logger.debug.bind(logger);
export const info = logger.info.bind(logger);
export const warn = logger.warn.bind(logger);
export const error = logger.error.bind(logger);
export const metric = logger.metric.bind(logger);
export const measure = logger.measure.bind(logger);
